import type { TFunction } from 'i18next'
import { DateTime } from 'luxon'
import { z } from 'zod'

import {
  ActivationGroupState,
  ActivationProvider,
  ActivationScheduleType,
  ApiVolumeRangeUnit,
  PrequalificationLane,
  ResourceCategory,
} from '@/features/activationGroup/types'
import { nullIfEmpty } from '@/features/activationGroup/utils/nullIfEmpty'
import { MarketProgram } from '@/features/bidding/constants'
import type { Portfolio } from '@/features/bidding/types/bid'

const MAX_FILE_SIZE = 5242880
const MAX_REGULATION_OBJECT_CODE_LENGTH_FI = 18

export const getCreateActivationGroupSchema = (t: TFunction, portfolios: Portfolio[]) =>
  z
    .object({
      name: z.string().min(1, t('activation_groups.create_form.name_required_error')),
      portfolio: z.string().min(1, t('activation_groups.create_form.portfolio_required_error')),
      marketProgram: z.enum(MarketProgram, {
        error: (issue) =>
          issue.value === undefined
            ? t('activation_groups.create_form.market_program_required_error')
            : t('activation_groups.create_form.market_program_invalid_error'),
      }),
      activationProvider: z.enum(ActivationProvider).nullable(),
      resourceCategory: z.enum(ResourceCategory),
      biddableVolumeRange: z
        .object({
          min: z.coerce
            .number()
            .gt(0, t('activation_groups.create_form.biddable_volume_range_min_greater_than_zero_error')),
          max: z.coerce.number(),
          unit: z.enum(ApiVolumeRangeUnit, {
            error: t('activation_groups.create_form.biddable_volume_range_unit_invalid_error'),
          }),
        })
        .refine(
          (data) => {
            if (data?.min) {
              return data.max > data.min
            }

            return true
          },
          {
            message: t('activation_groups.create_form.biddable_volume_range_max_greater_than_min_error'),
            path: ['max'],
          },
        ),
      regulationObjectCode: z.string().transform(nullIfEmpty).nullable(),
      transmissionArea: z.string().transform(nullIfEmpty).nullable(),
      tradingStrategy: z.string().transform(nullIfEmpty).nullable(),
      reportingCode: z.string().transform(nullIfEmpty).nullable(),
      externalCode: z.string().transform(nullIfEmpty).nullable(),
    })
    .refine(
      (data) => {
        const selectedPortfolio = portfolios.find((p) => p.code === data.portfolio)
        if (selectedPortfolio?.countryCode === 'FI' && data.regulationObjectCode) {
          return data.regulationObjectCode.length <= MAX_REGULATION_OBJECT_CODE_LENGTH_FI
        }
        return true
      },
      {
        message: t('activation_groups.create_form.regulation_object_code_max_length_error'),
        path: ['regulationObjectCode'],
      },
    )

export const getUpdateActivationGroupSchema = (t: TFunction, countryCode: string) =>
  z
    .object({
      name: z.string().min(1, t('activation_groups.create_form.name_required_error')),
      activationProvider: z.enum(ActivationProvider).nullable(),
      resourceCategory: z.enum(ResourceCategory),
      biddableVolumeRange: z
        .object({
          min: z.coerce
            .number()
            .gt(0, t('activation_groups.create_form.biddable_volume_range_min_greater_than_zero_error')),
          max: z.coerce.number(),
          unit: z.enum(ApiVolumeRangeUnit, {
            error: t('activation_groups.create_form.biddable_volume_range_unit_invalid_error'),
          }),
        })
        .refine(
          (data) => {
            if (data?.min) {
              return data.max > data.min
            }

            return true
          },
          {
            message: t('activation_groups.create_form.biddable_volume_range_max_greater_than_min_error'),
            path: ['max'],
          },
        ),
      state: z.enum(ActivationGroupState, {
        error: t('activation_groups.create_form.state_invalid_error'),
      }),
      regulationObjectCode: z.string().transform(nullIfEmpty).nullable(),
      transmissionArea: z.string().transform(nullIfEmpty).nullable(),
      tradingStrategy: z.string().transform(nullIfEmpty).nullable(),
      reportingCode: z.string().transform(nullIfEmpty).nullable(),
      externalCode: z.string().transform(nullIfEmpty).nullable(),
    })
    .refine(
      (data) => {
        if (countryCode === 'FI' && data.regulationObjectCode) {
          return data.regulationObjectCode.length <= MAX_REGULATION_OBJECT_CODE_LENGTH_FI
        }
        return true
      },
      {
        message: t('activation_groups.create_form.regulation_object_code_max_length_error'),
        path: ['regulationObjectCode'],
      },
    )

export const getAssignResourceToActivationGroupSchema = (t: TFunction) =>
  z.object({
    resourceUuid: z.guid({
      error: (issue) =>
        issue.input === undefined
          ? t('activation_groups.assign_resource_form.resource_required_error')
          : t('activation_groups.assign_resource_form.resource_invalid_error'),
    }),
    qualifiedCapacity: z.coerce
      .number({
        error: t('activation_groups.assign_resource_form.qualified_capacity_invalid_error'),
      })
      .int(t('activation_groups.assign_resource_form.qualified_capacity_invalid_error'))
      .min(0, t('activation_groups.assign_resource_form.qualified_capacity_out_of_bounds_error'))
      .max(999999999, t('activation_groups.assign_resource_form.qualified_capacity_out_of_bounds_error')),
    activationPriority: z.coerce
      .number({
        error: 'activation_groups.assign_resource_form.priority_invalid_error',
      })
      .int(t('activation_groups.assign_resource_form.priority_invalid_error'))
      .min(0, t('activation_groups.assign_resource_form.priority_out_of_bounds_error'))
      .max(1000, t('activation_groups.assign_resource_form.priority_out_of_bounds_error'))
      .nullable(),
    isActivatable: z.boolean(),
  })

export const getAssignResourcesToActivationGroupScheduleSchema = (t: TFunction) =>
  z.object({
    resourceUuids: z.array(
      z.uuid({
        error: (issue) =>
          issue.input === undefined
            ? t('activation_groups.schedules.resources.assign_dialog.resource_required_error')
            : t('activation_groups.schedules.resources.assign_dialog.resource_invalid_error'),
      }),
    ),
  })

export const getCreateScheduleSchema = (t: TFunction) =>
  z
    .object({
      activationGroupUuid: z.string(),
      startAt: z.coerce.date({
        error: (issue) => {
          if (issue.code === 'invalid_type') {
            return {
              message: t('activation_groups.create_schedule_form.start_at_invalid_error'),
            }
          }

          return {
            message: issue.message!,
          }
        },
      }),
      endAt: z.coerce.date({
        error: (issue) => {
          if (issue.code === 'invalid_type') {
            return {
              message: t('activation_groups.create_schedule_form.end_at_invalid_error'),
            }
          }

          return {
            message: issue.message!,
          }
        },
      }),
      soldCapacity: z.coerce
        .number({
          error: (issue) =>
            issue.input === undefined
              ? t('activation_groups.create_schedule_form.sold_capacity_required_error')
              : t('activation_groups.create_schedule_form.sold_capacity_invalid_error'),
        })
        .min(0, t('activation_groups.create_schedule_form.sold_capacity_min_error')),
    })
    .refine(
      ({ startAt, endAt }) => {
        if (!startAt || !endAt) {
          return true
        }

        const luxonStartAt = DateTime.fromISO(startAt.toISOString())
        const luxonEndAt = DateTime.fromISO(endAt.toISOString())

        return luxonStartAt.startOf('second') < luxonEndAt.startOf('second')
      },
      { message: t('activation_groups.create_schedule_form.end_at_before_start_at_error'), path: ['endAt'] },
    )

export const getGenericCreateScheduleSchema = (t: TFunction) =>
  z
    .object({
      activationGroupUuid: z.string(),
      activationTriggerChannel: z
        .number({
          error: t('activation_groups.create_schedule_form.activation_trigger_channel_required_error'),
        })
        .min(0, t('activation_groups.create_schedule_form.activation_trigger_channel_required_error')),
      scheduleType: z.enum(ActivationScheduleType, {
        error: t('activation_groups.create_schedule_form.schedule_type_required_error'),
      }),
      startAt: z.coerce.date({
        error: (error) => {
          if (error.code === 'invalid_type') {
            return {
              message: t('activation_groups.create_schedule_form.start_at_invalid_error'),
            }
          }

          return {
            message: error.message!,
          }
        },
      }),
      endAt: z.coerce.date({
        error: (error) => {
          if (error.code === 'invalid_type') {
            return {
              message: t('activation_groups.create_schedule_form.end_at_invalid_error'),
            }
          }

          return {
            message: error.message!,
          }
        },
      }),
      volume: z.coerce
        .number({
          error: (issue) =>
            issue.input
              ? t('activation_groups.create_schedule_form.volume_required_error')
              : t('activation_groups.create_schedule_form.volume_invalid_error'),
        })
        .min(0, t('activation_groups.create_schedule_form.volume_min_error')),
      includeAllResources: z.boolean(),
    })
    .refine(
      ({ startAt, endAt }) => {
        if (!startAt || !endAt) {
          return true
        }

        const luxonStartAt = DateTime.fromISO(startAt.toISOString())
        const luxonEndAt = DateTime.fromISO(endAt.toISOString())

        return luxonStartAt.startOf('second') < luxonEndAt.startOf('second')
      },
      { message: t('activation_groups.create_schedule_form.end_at_before_start_at_error'), path: ['endAt'] },
    )
    .refine(
      ({ scheduleType, activationTriggerChannel }) => {
        return !(scheduleType === ActivationScheduleType.PQ && activationTriggerChannel === 0)
      },
      {
        message: t('activation_groups.create_schedule_form.pq_activation_trigger_channel_illegal_error'),
        path: ['activationTriggerChannel'],
      },
    )
    .refine(
      ({ scheduleType, activationTriggerChannel }) => {
        return !(scheduleType === ActivationScheduleType.DEMO && activationTriggerChannel !== 0)
      },
      {
        message: t('activation_groups.create_schedule_form.demo_activation_trigger_channel_illegal_error'),
        path: ['activationTriggerChannel'],
      },
    )

export const getCreatePrequalificationPatternSchema = (t: TFunction) =>
  z.object({
    activationGroupUuid: z.string(),
    name: z.string().min(1, t('activation_groups.create_form.name_required_error')),
    pattern: z
      .any()
      .refine(Boolean, t('activation_groups.create_prequalification_pattern_form.csv_required_error'))
      .refine(
        (file) => file?.size <= MAX_FILE_SIZE,
        t('activation_groups.create_prequalification_pattern_form.file_too_big_error'),
      )
      .refine(
        (file) => file?.type === 'text/csv',
        t('activation_groups.create_prequalification_pattern_form.csv_required_error'),
      ),
  })

// eslint-disable-next-line sonarjs/no-identical-functions
export const getGenericCreatePrequalificationPatternSchema = (t: TFunction) =>
  z.object({
    activationGroupUuid: z.string(),
    name: z.string().min(1, t('activation_groups.create_form.name_required_error')),
    pattern: z
      .any()
      .refine(Boolean, t('activation_groups.create_prequalification_pattern_form.csv_required_error'))
      .refine(
        (file) => file?.size <= MAX_FILE_SIZE,
        t('activation_groups.create_prequalification_pattern_form.file_too_big_error'),
      )
      .refine(
        (file) => file?.type === 'text/csv',
        t('activation_groups.create_prequalification_pattern_form.csv_required_error'),
      ),
  })

export const getStartPrequalificationSchema = (t: TFunction) =>
  z.object({
    patternUuid: z.string({
      error: t('activation_groups.start_prequalification_form.pattern_required_error'),
    }),
    prequalificationLane: z.enum(PrequalificationLane, {
      error: t('activation_groups.start_prequalification_form.prequalification_lane_required_error'),
    }),
  })

export const getGenericStartPrequalificationSchema = (t: TFunction) =>
  z.object({
    patternUuid: z.string({
      error: t('activation_groups.start_prequalification_form.pattern_required_error'),
    }),
    activationTriggerChannel: z.number({
      error: t('activation_groups.start_prequalification_form.activation_trigger_channel_required_error'),
    }),
  })

export const getAbortPrequalificationSchema = (t: TFunction) =>
  z.object({
    prequalificationLane: z.enum(PrequalificationLane, {
      error: t('activation_groups.abort_prequalification_form.prequalification_lane_required_error'),
    }),
  })
